import { API, deleteRequest, getRequest, patchRequest, postRequest } from '@/api/api';
import { invalidateAuthorizedFile } from '@/utils/file-links';
import { isVideoAttachment } from '@/utils/video-attachments';

import {
  type EventResponse,
  EVENTS,
  type EventsListResponse,
  type GalleryImage,
  type GuestSuggestionsResponseDto,
} from './events.dto';
import { mapGuestSuggestion, type PastGuest } from './events.mappers';

/**
 * The events in the person's own space, like the first day of a temporary contract booked before
 * it starts. That space also lists every vessel event the person is seated on, so only the ones it
 * holds itself are kept.
 */
export const getPersonalEvents = async (personId: string): Promise<EventResponse[]> => {
  const { data } = await getRequest<EventsListResponse>(
    API.ACTIVITY,
    '/events?limit=1000&page=1',
    false,
    undefined,
    personId,
  );
  return data.items.filter((event) => event.vessel_id === personId);
};

export const getEventByRoom = async (tempChatId: string): Promise<EventResponse> => {
  const response = await getRequest<EventResponse>(API.ACTIVITY, `/events/by-room/${tempChatId}`);
  return response.data;
};

/**
 * Fetch one event without touching the global store (a sub-activity is itself an event).
 * `vesselId` reads another of your vessels' event; the active vessel's when left out.
 */
export const getEventDetail = async (id: string, vesselId?: string): Promise<EventResponse> => {
  const response = await getRequest<EventResponse>(
    API.ACTIVITY,
    `/events/${id}`,
    false,
    undefined,
    vesselId,
  );
  return response.data;
};

export const getOutOfWaterStatusForVessels = async (ids: string[], date: string) => {
  const response = await getRequest<{ [key: string]: boolean }>(
    API.ACTIVITY,
    `/events/is-out-of-water`,
    false,
    { ids, date },
  );
  return response.data;
};

export const getOutOfWaterEndForVessel = async (
  vesselId: string,
  at: string,
): Promise<string | null> => {
  const response = await getRequest<EventsListResponse>(
    API.ACTIVITY,
    `/events?limit=1000&page=1&type=${EVENTS.OUT_OF_WATER}`,
    false,
    undefined,
    vesselId,
  );

  const now = new Date(at).getTime();
  const ends = (response.data.items ?? [])
    .map((event) => event.time_slot)
    .filter((slot): slot is NonNullable<typeof slot> => Boolean(slot?.start && slot?.end))
    .filter((slot) => new Date(slot.start).getTime() <= now && new Date(slot.end).getTime() >= now)
    .map((slot) => new Date(slot.end).getTime());

  return ends.length ? new Date(Math.max(...ends)).toISOString() : null;
};

export const uploadCoverImage = async (id: string, file: File) => {
  const formData = new FormData();
  formData.append('image', file);

  await patchRequest(API.ACTIVITY, `/events/${id}/cover-image`, formData);
  invalidateAuthorizedFile();
};

export const uploadEventAttachments = async (id: string, file: File[]) => {
  const formData = new FormData();
  file.forEach((f) => formData.append('attachment', f));

  await patchRequest(API.ACTIVITY, `/events/${id}/attachments`, formData);
};

export const deleteEventAttachment = async (id: string, attachmentId: string, vesselId: string) => {
  await deleteRequest(API.ACTIVITY, `/events/${id}/attachment`, {
    id: attachmentId,
    vesselID: vesselId,
  });
};

export const cancelEvent = async (id: string, cancel_reason?: string): Promise<void> => {
  await patchRequest(API.ACTIVITY, `/events/${id}/cancel`, cancel_reason ? { cancel_reason } : {});
};

export const renewEvent = async (id: string): Promise<EventResponse> => {
  const response = await patchRequest<EventResponse, object>(
    API.ACTIVITY,
    `/events/${id}/renew`,
    {},
  );
  return response.data;
};

export type ParticipantStatus = 'accepted' | 'declined' | 'maybe';

export const setParticipantStatus = async (
  id: string,
  status: ParticipantStatus,
  declineReason?: string,
) => {
  await patchRequest(API.ACTIVITY, `/events/${id}/participant/status`, {
    status,
    ...(declineReason ? { decline_reason: declineReason } : {}),
  });
};

/** Changes how many guests a guest participant brings, on the event and its sub-events. */
export const setParticipantAdditionalGuests = async (
  id: string,
  participantId: string,
  additionalGuests: number,
) => {
  await patchRequest(
    API.ACTIVITY,
    `/events/${id}/participants/${participantId}/additional-guests`,
    {
      additional_guests: additionalGuests,
    },
  );
};

export const uploadGalleryImage = async (id: string, file: File, name?: string) => {
  const formData = new FormData();
  formData.append('image', file);
  if (name) {
    formData.append('name', name);
  }

  await postRequest(API.ACTIVITY, `/events/${id}/gallery`, formData);
};

/**
 * `scope: 'personal'` keeps the photo in the uploader's own gallery alone; left
 * out, it is linked into every gallery that may see it — the uploader's, the
 * vessel's, and each participant's of the event.
 */
export const uploadVesselGalleryImage = async (
  file: File,
  name?: string,
  eventId?: string,
  scope?: 'personal',
) => {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('name', name?.trim() || file.name);
  if (eventId) {
    formData.append('event_id', eventId);
  }
  if (scope) {
    formData.append('scope', scope);
  }

  // A 100 MB video never finishes inside the default 15-second timeout.
  await postRequest(API.ACTIVITY, `/gallery`, formData, isVideoAttachment(file.type, file.name));
};

export const deleteVesselGalleryImage = async (imageId: string) => {
  await deleteRequest(API.ACTIVITY, `/gallery/${imageId}`);
};

export const getVesselGallery = async (): Promise<GalleryImage[]> => {
  const response = await getRequest<GalleryImage[]>(API.ACTIVITY, `/gallery`);
  return response.data;
};

// `/gallery` returns [] regardless of contents; the images for a given album
// (event) are only served per-event.
export const getEventGallery = async (eventId: string): Promise<GalleryImage[]> => {
  const response = await getRequest<GalleryImage[]>(API.ACTIVITY, `/events/${eventId}/gallery`);
  return response.data;
};

/** Guests of the vessel's past events, most frequent and recent first (empty on a cold start). */
export const getGuestSuggestions = async (limit = 10): Promise<PastGuest[]> => {
  const response = await getRequest<GuestSuggestionsResponseDto>(
    API.ACTIVITY,
    `/events/guest-suggestions`,
    false,
    { limit },
  );
  return (response.data.suggestions ?? []).map(mapGuestSuggestion);
};
